import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  acceptStaffInvitation,
  declineStaffInvitation,
  fetchMyStaffInvitations,
} from '../../api/staff';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { getFriendlyError } from '../../utils/friendlyError';

const StaffMembershipInvites = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { refreshUser } = useAuth();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['staff-membership-invitations'],
    queryFn: fetchMyStaffInvitations,
  });

  const invitations = data?.invitations || [];

  const acceptMut = useMutation({
    mutationFn: acceptStaffInvitation,
    onSuccess: async (res) => {
      showToast(res.message || t('staff.invitations.accepted'), 'success');
      await refreshUser?.();
      queryClient.invalidateQueries({ queryKey: ['staff-membership-invitations'] });
      queryClient.invalidateQueries({ queryKey: ['staff-dashboard'] });
    },
    onError: (err) => showToast(getFriendlyError(err), 'error'),
  });

  const declineMut = useMutation({
    mutationFn: declineStaffInvitation,
    onSuccess: async (res) => {
      showToast(res.message || t('staff.invitations.declined'), 'success');
      await refreshUser?.();
      queryClient.invalidateQueries({ queryKey: ['staff-membership-invitations'] });
    },
    onError: (err) => showToast(getFriendlyError(err), 'error'),
  });

  if (isLoading || !invitations.length) return null;

  const busy = acceptMut.isPending || declineMut.isPending;

  return (
    <section className="mb-8 rounded-xl border border-glow-700/40 bg-glow-950/20 p-5">
      <h2 className="font-semibold text-glow-200">{t('staff.invitations.title')}</h2>
      <p className="mt-1 text-sm text-zinc-500">{t('staff.invitations.desc')}</p>
      <div className="mt-4 space-y-3">
        {invitations.map((invite) => (
          <article
            key={invite.permissionId}
            className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900"
          >
            <div>
              <p className="font-medium">{invite.businessName}</p>
              {invite.city && <p className="text-sm text-zinc-500">{invite.city}</p>}
              <p className="mt-1 text-xs text-zinc-500">
                {new Date(invite.requestedAt).toLocaleString()}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => declineMut.mutate(invite.permissionId)}
                className="rounded-lg border border-zinc-300 px-3 py-2 text-sm dark:border-zinc-700"
              >
                {t('common.reject')}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => acceptMut.mutate(invite.permissionId)}
                className="rounded-lg bg-glow-600 px-3 py-2 text-sm text-white hover:bg-glow-500"
              >
                {t('staff.invitations.acceptJoin')}
              </button>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};

export default StaffMembershipInvites;
